import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { IoMdArrowBack } from "react-icons/io";
import Select from "react-select";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import {
  REPEAT_UNIT_OPTIONS,
  buildSimpleOptions,
  normaliseReminderDays,
} from "../hrRecordsHelpers";
import "../../forms/UserForm.css";
import "../hrRecords.css";

function TrainingMasterFormPage({ isSidebarExpanded, mode = "create" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";
  const [formData, setFormData] = useState({
    trainingCode: "",
    trainingName: "",
    trainingCategory: "",
    standardBody: "",
    standardRefNo: "",
    standardSectionNo: "",
    standardDescription: "",
    repeatEvery: "",
    repeatUnit: "",
    graceDays: "",
    reminderDaysBefore: "",
    defaultDurationHours: "",
    description: "",
    isMandatory: false,
    isActive: true,
  });
  const [lockedFields, setLockedFields] = useState([]);
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchLookups = async () => {
    const [standardsResponse, categoriesResponse] = await Promise.all([
      hrRecordsApi.listTrainingStandards(),
      hrRecordsApi.getTrainingCategories(),
    ]);

    setStandards(buildSimpleOptions(standardsResponse || [], "standard_body", "display_name"));
    setCategories(
      buildSimpleOptions(categoriesResponse || [], "category_name", "category_name"),
    );
  };

  const fetchMaster = async () => {
    if (!isEdit || !id) return;
    const response = await hrRecordsApi.getTrainingMaster(id);

    setLockedFields(response.locked_fields || []);
    setFormData({
      trainingCode: response.training_code || "",
      trainingName: response.training_name || "",
      trainingCategory: response.training_category || "",
      standardBody: response.standard_body || "",
      standardRefNo: response.standard_ref_no || "",
      standardSectionNo: response.standard_section_no || "",
      standardDescription: response.standard_description || "",
      repeatEvery: response.repeat_every || "",
      repeatUnit: response.repeat_unit || "",
      graceDays: response.grace_days || "",
      reminderDaysBefore: normaliseReminderDays(response.reminder_days_before),
      defaultDurationHours: response.default_duration_hours || "",
      description: response.description || "",
      isMandatory: Boolean(response.is_mandatory),
      isActive: Boolean(response.is_active),
    });
  };

  useEffect(() => {
    const run = async () => {
      try {
        setPageLoading(true);
        await fetchLookups();
        await fetchMaster();
      } catch (error) {
        console.error("Failed to load training master form:", error);
        showSnackbar("Failed to load training master data", "error");
      } finally {
        setPageLoading(false);
      }
    };
    run();
  }, [id, isEdit]);

  const isLocked = (field) => lockedFields.includes(field);

  const selectedStandard = useMemo(
    () =>
      standards.find(
        (standard) => String(standard.value) === String(formData.standardBody),
      ) || null,
    [standards, formData.standardBody],
  );
  const standardRefNoLabel =
    selectedStandard?.ref_no_label || "Standard Reference No.";
  const standardSectionNoLabel =
    selectedStandard?.section_label || "Standard Section No.";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.trainingName) {
      nextErrors.trainingName = "Training name is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const payload = {
    training_code: formData.trainingCode || undefined,
    training_name: formData.trainingName,
    training_category: formData.trainingCategory || undefined,
    standard_body: formData.standardBody || undefined,
    standard_ref_no: formData.standardRefNo || undefined,
    standard_section_no: formData.standardSectionNo || undefined,
    standard_description: formData.standardDescription || undefined,
    repeat_every: formData.repeatEvery || undefined,
    repeat_unit: formData.repeatUnit || undefined,
    grace_days: formData.graceDays || undefined,
    reminder_days_before: formData.reminderDaysBefore || undefined,
    default_duration_hours: formData.defaultDurationHours || undefined,
    description: formData.description || undefined,
    is_mandatory: formData.isMandatory,
    is_active: formData.isActive,
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      if (isEdit) {
        await hrRecordsApi.updateTrainingMaster(id, payload);
      } else {
        await hrRecordsApi.createTrainingMaster(payload);
      }

      showSnackbar(`Training master ${isEdit ? "updated" : "created"} successfully`);
      window.setTimeout(() => {
        navigate("/training/catalouge");
      }, 900);
    } catch (error) {
      console.error("Failed to save training master:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to save training master",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <div className="data-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate("/training/catalouge")}
                  />
                </Tooltip>
                {isEdit ? "Edit Training" : "Add New Training"}
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-form-body">
            {pageLoading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <div className="new-data-form hr-records-form-grid-two">
                  <div className="form-group">
                    <label className="hr_training_labels">Training Code</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.trainingCode}
                        onChange={(event) => handleChange("trainingCode", event.target.value)}
                        placeholder="Enter training code"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">
                      Training Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.trainingName}
                        onChange={(event) => handleChange("trainingName", event.target.value)}
                        placeholder="Enter training name"
                      />
                    </div>
                    {errors.trainingName && (
                      <div className="error-message">{errors.trainingName}</div>
                    )}
                  </div>

                  <div className="form-group-select">
                    <label className="hr_training_labels">Training Category</label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        className="hr-records-select"
                        options={categories}
                        value={categories.find(
                          (category) => category.value === formData.trainingCategory,
                        )}
                        onChange={(option) =>
                          handleChange("trainingCategory", option?.value || "")
                        }
                        isClearable
                        placeholder="Select category"
                      />
                    </div>
                  </div>

                  <div className="form-group-select">
                    <label className="hr_training_labels">Standard Body</label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        className="hr-records-select"
                        options={standards}
                        value={standards.find(
                          (standard) => standard.value === formData.standardBody,
                        )}
                        onChange={(option) =>
                          handleChange("standardBody", option?.value || "")
                        }
                        isClearable
                        isDisabled={isLocked("standard_body")}
                        placeholder="Select standard body"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">{standardRefNoLabel}</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.standardRefNo}
                        onChange={(event) =>
                          handleChange("standardRefNo", event.target.value)
                        }
                        disabled={isLocked("standard_ref_no")}
                        placeholder="Enter standard reference number"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">{standardSectionNoLabel}</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.standardSectionNo}
                        onChange={(event) =>
                          handleChange("standardSectionNo", event.target.value)
                        }
                        disabled={isLocked("standard_section_no")}
                        placeholder="Enter standard section number"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Standard Description</label>
                    <div className="input-group hr-records-form-control">
                      <textarea
                        rows={2}
                        value={formData.standardDescription}
                        onChange={(event) =>
                          handleChange("standardDescription", event.target.value)
                        }
                        style={{ width: "100%", resize: "vertical" }}
                        placeholder="Enter standard description"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Repeat Every</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="number"
                        value={formData.repeatEvery}
                        onChange={(event) => handleChange("repeatEvery", event.target.value)}
                        disabled={isLocked("repeat_every")}
                      />
                    </div>
                  </div>

                  <div className="form-group-select">
                    <label className="hr_training_labels">Repeat Unit</label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        className="hr-records-select"
                        options={REPEAT_UNIT_OPTIONS}
                        value={REPEAT_UNIT_OPTIONS.find(
                          (option) => option.value === formData.repeatUnit,
                        )}
                        onChange={(option) =>
                          handleChange("repeatUnit", option?.value || "")
                        }
                        isClearable
                        isDisabled={isLocked("repeat_unit")}
                        placeholder="Select repeat unit"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Grace Days</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="number"
                        value={formData.graceDays}
                        onChange={(event) => handleChange("graceDays", event.target.value)}
                        disabled={isLocked("grace_days")}
                        placeholder="Enter grace days"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Reminder Days Before</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.reminderDaysBefore}
                        onChange={(event) =>
                          handleChange("reminderDaysBefore", event.target.value)
                        }
                        placeholder="e.g. 30,15,7"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Default Duration Hours</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="number"
                        value={formData.defaultDurationHours}
                        onChange={(event) =>
                          handleChange("defaultDurationHours", event.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Description</label>
                    <div className="input-group hr-records-form-control">
                      <textarea
                        rows={3}
                        value={formData.description}
                        onChange={(event) => handleChange("description", event.target.value)}
                        style={{ width: "100%", resize: "vertical" }}
                        placeholder="Enter description"
                      />
                    </div>
                  </div>

                  <div className="form-group hr-records-checkbox-row">
                    <label className="hr_training_labels">
                      Mandatory Training
                    </label>
                    <div className="input-group hr-records-checkbox-control">
                      <input
                        type="checkbox"
                        checked={formData.isMandatory}
                        onChange={(event) =>
                          handleChange("isMandatory", event.target.checked)
                        }
                      />
                    </div>
                  </div>

                  <div className="form-group hr-records-checkbox-row">
                    <label className="hr_training_labels">Active
                    </label>
                    <div className="input-group hr-records-checkbox-control">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(event) =>
                          handleChange("isActive", event.target.checked)
                        }
                      />
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSave}
                disabled={loading || pageLoading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={() => navigate("/training/catalouge")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingMasterFormPage;
